import { useContext, useEffect, useRef, useState } from "react";
import ServiceComment from "../../../models/ServiceComment";
import { ApiFetch, ApiPost, ApiPatch } from "../../Common/Api";
import { Pageable, ServiceRequestService } from "../../../models";
import UserContext, { Roles } from "../../../UserContext";
import { ServiceRequestContext } from "../ServiceRequestContext";
import useIsVisible from "../../Common/IsVisible";

type ServiceCommentsProps = { service: ServiceRequestService, isGeneralView: boolean};

const ServiceComments = ( { service, isGeneralView } : ServiceCommentsProps) => {

    const userContext = useContext(UserContext);
    const { refreshList, services, setServices } = useContext(ServiceRequestContext);
    const isProvider = userContext.roles.includes(Roles.Provider);
    const provider: ServiceRequestServiceCommentProvider | undefined = isProvider ? {userID: userContext.user.userID ?? 0, name: userContext.user?.name ?? ''} : undefined;
    const [selectedProvider, setSelectedProvider] = useState<number>(provider ? provider.userID : 0);
    const [providers, setProviders] = useState<ServiceRequestServiceCommentProvider[]>(provider ? [provider] : []);
    const [comments, setComments] = useState<ServiceComment[] | undefined>(undefined);
    const [newCommentText, setNewCommentText] = useState<string>("");
    const [loadingCounter, setLoadingCounter] = useState<number>(0);
    const discardPollingUpdate = useRef<boolean>(false);
    const [error, setError] = useState<string>("");
    const [areCommentsVisible, areCommentsVisibleRef] = useIsVisible(() => {
        if (service.unreadCommentCount !== undefined && service.unreadCommentCount > 0)
            updateReadStatus(service.serviceRequestServiceID, isGeneralView ? 0 : selectedProvider)
    });
    const commentsRef = useRef<ServiceComment[] | undefined>(comments);
    useEffect(() => {
        commentsRef.current = comments;
    }, [comments]);

    useEffect(() => {
        if (!isProvider && !isGeneralView) {
            setLoadingCounter(prev => prev + 1);
            ApiFetch<ServiceRequestServiceCommentProvider[]>('ServiceRequestServiceComment/providers', {serviceRequestServiceID: service.serviceRequestServiceID})
                .then((res) => setProviders(res))
                .catch(console.log)
                .finally(() => {
                    setLoadingCounter(prev => Math.max(0, prev - 1));
                })
        }
    }, []);
     useEffect(() => {
        const handler = setInterval(() => onPollComments(true), isGeneralView ? 600000 : 120000);
        if (selectedProvider && !isGeneralView) {
            getComments(service.serviceRequestServiceID, undefined, false, true, true, false);
        }
        else if (isGeneralView) {
            getComments(service.serviceRequestServiceID, undefined, true, true, true, false);
        }
        return () => {
            clearInterval(handler); 
        };
    }, []);

    const getComments = (serviceRequestServiceID?: number, providerID?: number, includeGeneralComments?: boolean, useLoadingCounter = false, firstRun = false, pollingUpdate = false) => {
        if (pollingUpdate) {
            if (discardPollingUpdate.current)
                return;
        } else {
            discardPollingUpdate.current = true;
        }
        if (useLoadingCounter) setLoadingCounter(prev => prev + 1);
        ApiFetch<Pageable<ServiceComment>>('ServiceRequestServiceComment', { serviceRequestServiceID, providerID, isGeneralComment: includeGeneralComments, pageSize: 1000, rowIndex: 0 })
        .then((res) => {
            if (pollingUpdate && discardPollingUpdate.current) return;
            let diff = 0;
            let shouldUpdateCount = false; //don't do the comment count change logic on the initial call
            const prev = commentsRef.current;
            if (prev !== undefined){ 
                diff = res.results.length - prev.length;
                if (prev.length > 0 && prev[prev.length - 1].createdOn === undefined) diff += 1;
                shouldUpdateCount = !firstRun && diff >= 1;
            }
            setComments(res.results);
            if (shouldUpdateCount) {
                setServices(prev => prev.map(s => s.serviceRequestServiceID === service.serviceRequestServiceID && s.totalCommentCount !== undefined
                    ? { ...s, totalCommentCount: s.totalCommentCount + diff }
                    : s
                ));
            }

        })
        // eslint-disable-next-line no-console
        .catch(console.log)
        .finally(() => {
            discardPollingUpdate.current = false;
            if (useLoadingCounter) setLoadingCounter(prev => Math.max(0, prev - 1));
        })
    };
    const addComment = (comment: ServiceComment) => {
        if (error) setError("");
        ApiPost<ServiceComment>('ServiceRequestServiceComment', undefined, comment)
            .then((res) => {
                setNewCommentText("");
                onPollComments(false);
            })
            .catch((e) => {
                console.log(e);
                setError("There was an error sending your message.");
                setComments(comments?.filter(c => c.createdOn));
            });
    }
    const changeSelectedProvider = (providerID: number) => {
        setSelectedProvider(providerID);
        getComments(service.serviceRequestServiceID, providerID, false, true, true);
    }
    const onAddComment = (commentText: string) => {
        let comment : ServiceComment = {
            serviceRequestServiceID: service.serviceRequestServiceID,
            comment: commentText,
            ProviderUserID: selectedProvider,
            user: {userID: userContext.user.userID, name: userContext.user.name},
            isGeneralComment: false
        };
        if (isGeneralView) {
            comment.ProviderUserID = undefined;
            comment.isGeneralComment = true;
        }
        setComments((currentComments) => [...(currentComments ?? []), comment]);
        addComment(comment);
    }
    const updateReadStatus = (serviceRequestServiceID?: number, providerID?: number) => {
          ApiPatch<any>('ServiceRequestServiceComment/read', undefined, { serviceRequestServiceID, providerUserID: providerID ?? 0})
            .then((res) => {
                if (res) {
                    setServices(prev => prev.map(s => s.serviceRequestServiceID === service.serviceRequestServiceID && s.totalCommentCount !== undefined
                        ? { ...s,  unreadCommentCount: res.unreadCommentCount }
                        : s
                    ));
                }
            })
            .catch(console.log);
    }
    const onPollComments = (isPollingUpdate: boolean) => {
        if (isGeneralView) {
            getComments(service.serviceRequestServiceID, undefined, true, false, false, isPollingUpdate);
        } else if (selectedProvider) {
            getComments(service.serviceRequestServiceID, selectedProvider, false, false, false, isPollingUpdate);
        }
    }
    const isSending = ()  => comments && comments.length > 0 && comments[comments.length - 1].createdOn === undefined;
    const isNewCommentValid = () => {
        return newCommentText.trim().length > 0;
    }
    const displayTimestamp = (timestamp: Date) => {
        const date = new Date(timestamp);
        const today = new Date();
        if (date.getFullYear() === today.getFullYear()
            && date.getMonth() === today.getMonth() 
            && date.getDate() === today.getDate()) {
            return date.toLocaleTimeString();
        }
        return date.toDateString();
    }
    if (service.serviceRequestServiceID === undefined) return null;
    if (loadingCounter > 0) 
        return (<div className="no-records">
            <img src="/spinning-circles.svg" alt="loading" />
        </div>);
    let noCommentsHTML;
    if (comments?.length === 0) {
        if (isGeneralView)
            noCommentsHTML = (<p>There's currently no additional information for this service posting</p>)
        else if (isProvider)
            noCommentsHTML = (<p>You can start a conversation with a care manager here.</p>)
        else if (!selectedProvider) {
            noCommentsHTML = (<p>Please select a provider.</p>)
        } else {
            noCommentsHTML = (<p>No comments found.</p>)
        }
    }
    return (
        <main className='serviceComments'>
            {provider === undefined && !isGeneralView ?
            <label className='provider-label'>
                Provider:
                <select value={selectedProvider ?? 0} className='provider' onChange={(e) => changeSelectedProvider(parseInt(e.target.value))}>
                    <option value="0" disabled>Select a provider</option>
                    {providers?.map((provider, index) => (
                        <option key={provider.name} value={provider.userID}>{provider.name}</option>
                    ))}
                </select>
            </label>
            : null }
            <div className='thread'>
            {noCommentsHTML}
            {comments?.map((comment, i) => (
                <article ref={i === 0 ? areCommentsVisibleRef : null} className='comment' key={(comment.createdOn ?? '') + (comment.user?.name ?? '')}>
                    <header>
                        <section>
                            <h5 className='name'>{comment.user?.userID === userContext.user.userID ? comment.user?.name + " (You)" : comment.user?.name}</h5>
                            <h6 className='role'>{comment.user?.role}</h6>
                        </section>
                        {
                            comment.createdOn ? 
                            (<time className='time' dateTime={comment.createdOn?.toLocaleString() ?? new Date().toLocaleString()} title={comment.createdOn?.toLocaleString()}>{displayTimestamp(comment.createdOn ?? new Date())}</time>) :
                            (<span className='time'>sending...</span>)
                        }
                    </header>
                    <p>{comment.comment}</p>
                </article>
            ))}
            </div>
            {providers === undefined || (isGeneralView && isProvider) || (!isGeneralView && selectedProvider <= 0) ? null : (
                <div className='newComment'>
                    <textarea disabled={isSending()} value={newCommentText} onChange={(e) => setNewCommentText(e.target.value)} className='new-comment-text' placeholder='Type your comment here'>
    
                    </textarea>
                    <button disabled={isSending() || !isNewCommentValid} onClick={() => onAddComment(newCommentText)} className="new-comment-button">
                        {isSending() ? <div className="no-records new-comment-loading">
                                <img src="/spinning-circles.svg" alt="loading" />
                            </div> : "Send"}
                    </button>
                </div>
            )}
            {error !== "" ? <p className="new-comment-error">{error}</p> : null}
        </main>
    )
}

export default ServiceComments;